import { useState } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";

/**
 * Modal dialog for admin to invite a new client. Sends them a welcome email
 * with a one-click "Set your password" link (72h expiry).
 */
const InviteClientButton = ({ onInvited }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", phone: "", preferred_language: "en", send_welcome_email: true });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clients/invite", form);
      if (data.welcome_email_sent) {
        toast.success(`Welcome email sent to ${form.email}`);
      } else if (data.temporary_password) {
        toast.success(`Account created. Temp password: ${data.temporary_password}`);
      }
      setForm({ email: "", full_name: "", phone: "", preferred_language: "en", send_welcome_email: true });
      setOpen(false);
      if (onInvited) onInvited(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="rn3-btn-gold" data-testid="invite-client-btn">
        <UserPlus className="w-4 h-4" /> Invite Client
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="invite-modal">
          <div className="bg-white max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-[#737373] hover:text-[#0A0A0A]" data-testid="invite-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <div className="rn3-eyebrow flex items-center gap-2"><UserPlus className="w-4 h-4" /> New Client</div>
            <h2 className="font-serif-display text-2xl mt-2">Invite to portal</h2>
            <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
              <div>
                <label className="rn3-eyebrow block mb-1">Full Name</label>
                <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rn3-input" data-testid="invite-name" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-1">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="invite-email" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-1">Phone (optional)</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rn3-input" data-testid="invite-phone" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-1">Preferred Language</label>
                <select value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className="rn3-input" data-testid="invite-lang">
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
              <label className="flex items-start gap-2 text-xs text-[#404040]">
                <input
                  type="checkbox"
                  checked={form.send_welcome_email}
                  onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
                  className="mt-0.5"
                  data-testid="invite-email-toggle"
                />
                <span>Send welcome email with set-password link (recommended). Uncheck to receive a temporary password to share manually.</span>
              </label>
              <button disabled={busy} type="submit" className="rn3-btn-primary w-full justify-center" data-testid="invite-submit">
                {busy ? "Sending…" : "Send Invite"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteClientButton;
